import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';

import {
  BoxExpand,
  Button,
  ButtonsWrapper,
  CheckboxInput,
  Drawer,
  Form,
  Loader,
} from '@thinknet-gmbh/platform-common-ui';

import { useGetAllPlatformCompanies } from 'src/hooks/companies/get-all-platform-companies.hook';
import { useGetPlatformCompanyBusinessAddresses } from 'src/hooks/companies/get-platform-company-business-addresses.hook';
import { useGetPlatformCompanyDepartments } from 'src/hooks/companies/get-platform-company-departments.hook';
import { useGetPlatformCompanyLocations } from 'src/hooks/companies/get-platform-company-locations.hook';
import {
  IFilterData,
  ITableFilterProps,
} from 'src/pages/employee/sub-pages/employee-list/employee-list.interfaces';

import { BoxExpandContent } from './employee-table-filter.styles';

const EmployeeTableFilter = ({ isOpened, onClose, setFilters, filters }: ITableFilterProps) => {
  const { t } = useTranslation('common');

  const { companies, loading: companiesLoading } = useGetAllPlatformCompanies();
  const { companyLocations, loading: locationsLoading } = useGetPlatformCompanyLocations();
  const {
    companyBusinessAddresses,
    loading: businessAddressesLoading,
  } = useGetPlatformCompanyBusinessAddresses();
  const { companyDepartments, loading: departmentsLoading } = useGetPlatformCompanyDepartments();

  const loading =
    companiesLoading || locationsLoading || businessAddressesLoading || departmentsLoading;

  const onFinish = (values: any) => {
    const { companiesIds = {}, companyLocationsIds = {}, companyDepartmentsIds = {} } = values;

    const selectedCompaniesIds = Object.keys(companiesIds).filter(key => !!companiesIds[key]);
    const selectedCompanyLocationsIds = Object.keys(companyLocationsIds).filter(
      key => !!companyLocationsIds[key]
    );
    const selectedDepartmentsIds = Object.keys(companyDepartmentsIds).filter(
      key => !!companyDepartmentsIds[key]
    );

    const isFiltersEmpty =
      isEmpty(selectedCompaniesIds) &&
      isEmpty(selectedCompanyLocationsIds) &&
      isEmpty(selectedDepartmentsIds);

    const newFilters = {
      ...filters,
      companyFilter: {
        companiesIds: selectedCompaniesIds,
        companyLocationsIds: selectedCompanyLocationsIds,
        companyDepartmentsIds: selectedDepartmentsIds,
      },
    } as IFilterData;

    if (isFiltersEmpty) {
      delete newFilters?.companyFilter;
    }

    setFilters(newFilters);
    onClose();
  };

  if (loading && isOpened) {
    return <Loader />;
  }

  return (
    <Drawer visible={isOpened} onClose={onClose} title={t('common:buttons.filterBy')}>
      <Form
        onFinish={onFinish}
        initialValues={
          filters.companyFilter
            ? {
                companiesIds: filters?.companyFilter?.companiesIds?.reduce(
                  (result, id) => ({ ...result, [id]: true }),
                  {}
                ),
                companyLocationsIds: filters?.companyFilter?.companyLocationsIds?.reduce(
                  (result, id) => ({ ...result, [id]: true }),
                  {}
                ),
                companyDepartmentsIds: filters?.companyFilter?.companyDepartmentsIds?.reduce(
                  (result, id) => ({ ...result, [id]: true }),
                  {}
                ),
              }
            : undefined
        }
      >
        <BoxExpand title={t('labels.companyName')} isExpandedWhenEdit={true}>
          <BoxExpandContent>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => {
                return companies.map((company: any) => (
                  <Form.Item
                    key={company._id}
                    name={['companiesIds', company._id]}
                    valuePropName="checked"
                  >
                    <CheckboxInput>{company.name}</CheckboxInput>
                  </Form.Item>
                ));
              }}
            </Form.Item>
          </BoxExpandContent>
        </BoxExpand>

        <BoxExpand title={t('labels.location')} isExpandedWhenEdit={true}>
          <BoxExpandContent>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => {
                const companiesIds = getFieldValue('companiesIds') || {};
                const selectedCompaniesIds = Object.keys(companiesIds).filter(
                  key => !!companiesIds[key]
                );

                let locations = companyLocations;

                if (selectedCompaniesIds.length) {
                  locations = companyLocations.filter(({ companyId }: any) =>
                    selectedCompaniesIds.includes(companyId)
                  );
                }

                return locations.map((location: any) => (
                  <Form.Item
                    key={location._id}
                    name={['companyLocationsIds', location._id]}
                    getValueFromEvent={e => {
                      const checked = e.target.checked;
                      if (checked) {
                        const companiesIds = getFieldValue('companiesIds');
                        setFieldsValue({
                          companiesIds: { ...companiesIds, [location.companyId]: true },
                        });
                      }
                      return checked;
                    }}
                    valuePropName="checked"
                  >
                    <CheckboxInput>{location.name}</CheckboxInput>
                  </Form.Item>
                ));
              }}
            </Form.Item>
          </BoxExpandContent>
        </BoxExpand>

        <BoxExpand title={t('labels.department')} isExpandedWhenEdit={true}>
          <BoxExpandContent>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => {
                const companyLocationsIds = getFieldValue('companyLocationsIds') || {};
                const companiesIds = getFieldValue('companiesIds') || {};

                const selectedCompanyLocationsIds = Object.keys(companyLocationsIds).filter(
                  key => !!companyLocationsIds[key]
                );
                const selectedCompaniesIds = Object.keys(companiesIds).filter(
                  key => !!companiesIds[key]
                );

                let departments = companyDepartments;

                if (selectedCompanyLocationsIds.length || selectedCompaniesIds.length) {
                  const activeBusinessAddresses = companyBusinessAddresses.filter(
                    ({ companyLocationId }: any) =>
                      selectedCompanyLocationsIds.includes(companyLocationId)
                  );
                  const activeBusinessAddressesIds = activeBusinessAddresses.map(
                    ({ _id }: any) => _id
                  );

                  departments = companyDepartments.filter(({ companyBusinessAddressId }: any) =>
                    activeBusinessAddressesIds.includes(companyBusinessAddressId)
                  );
                }

                return departments.map((department: any) => (
                  <Form.Item
                    key={department._id}
                    name={['companyDepartmentsIds', department._id]}
                    getValueFromEvent={e => {
                      const checked = e.target.checked;
                      if (checked) {
                        const companiesIds = getFieldValue('companiesIds') || {};
                        const companyLocationsIds = getFieldValue('companyLocationsIds') || {};

                        const businessAddress = companyBusinessAddresses.find(
                          ({ _id }: any) => _id === department.companyBusinessAddressId
                        );
                        const location = companyLocations.find(
                          ({ _id }: any) => _id === businessAddress.companyLocationId
                        );

                        setFieldsValue({
                          companiesIds: {
                            ...companiesIds,
                            [location.companyId]: true,
                          },
                          companyLocationsIds: {
                            ...companyLocationsIds,
                            [location._id]: true,
                          },
                        });
                      }
                      return checked;
                    }}
                    valuePropName="checked"
                  >
                    <CheckboxInput>{department.name}</CheckboxInput>
                  </Form.Item>
                ));
              }}
            </Form.Item>
          </BoxExpandContent>
        </BoxExpand>

        <ButtonsWrapper marginTop={16}>
          <Button type="primary" htmlType="submit">
            {t('common:buttons.save')}
          </Button>
          <Button
            onClick={() => {
              onClose();
              setFilters({ ...filters, companyFilter: null });
            }}
          >
            {t('common:buttons.cancel')}
          </Button>
        </ButtonsWrapper>
      </Form>
    </Drawer>
  );
};

export default EmployeeTableFilter;
